/**
 * The value input when a country/campaign scope is active.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Kept the SAME width and label length as the default field so the grid stays
 * aligned. Which scope is being edited is stated once in the banner above,
 * rather than repeated in this column header — where it truncated to
 * "Value for Fra…" and pushed the hint into three wrapped lines.
 */

import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';

import { Input } from '@/components/ui';
import type { ValueSource } from '@/features/tag-rule-scope';
import { cn } from '@/lib/utils';

/**
 * What the number under the box says.
 *
 * A bare "Inherited" left the reader unable to answer the only question they
 * have — is this MY number or somebody else's, and what happens if I type? So
 * each state names its origin AND the default it would fall back to.
 */
function hintFor(source: ValueSource, defaultValue: number): string {
  if (source === 'own') return `Set here · default ${defaultValue}`;
  if (source === 'country') return `From country · default ${defaultValue}`;
  return `Default ${defaultValue} · type to set your own`;
}

export const ScopedValueField: FC<{
  value: number;
  defaultValue: number;
  source: ValueSource;
  onChange: (value: number) => void;
  onReset: () => void;
  /** Set when the winning tier COMPARES — "Revenue (Last 7 days)". The input
   *  is replaced by the summary; typing a number would silently discard the
   *  comparison, so pinning happens through the override rows instead. */
  compareNote?: string;
}> = ({ value, defaultValue, source, onChange, onReset, compareNote }) => {
  const [local, setLocal] = useState(String(value));
  const lastSent = useRef(value);

  useEffect(() => {
    if (lastSent.current !== value) {
      lastSent.current = value;
      setLocal(String(value));
    }
  }, [value]);

  const handle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setLocal(raw);
      const num = raw === '' ? 0 : Number(raw);
      if (!Number.isNaN(num)) {
        lastSent.current = num;
        onChange(num);
      }
    },
    [onChange],
  );

  return (
    <div>
      <label
        className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground"
        title="Applies to the scope named above; every other scope is untouched."
      >
        Value
        {source === 'own' ? (
          <button
            type="button"
            onClick={onReset}
            title={`Remove this value so it inherits again (default ${defaultValue})`}
            aria-label="Reset to default"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" aria-hidden />
          </button>
        ) : null}
      </label>
      {compareNote ? (
        <p
          className={cn(
            'flex h-9 min-w-[80px] items-center truncate rounded-md border border-input bg-muted/40 px-2 text-xs',
            source === 'own' && 'border-primary/60',
          )}
          title={`This scope compares against ${compareNote}. Edit or pin a number in the override rows (All countries view).`}
        >
          → {compareNote}
        </p>
      ) : (
        <Input
          type="number"
          value={local}
          onChange={handle}
          className={cn('h-9 w-full min-w-[80px]', source === 'own' && 'border-primary/60')}
          aria-label="Value for the selected scope"
          placeholder="0"
        />
      )}
      <p
        className={cn(
          'mt-1 truncate text-[10px] leading-tight',
          source === 'own' ? 'font-medium text-primary' : 'text-muted-foreground',
        )}
        title={
          source === 'own'
            ? `Set for this scope. The default is ${defaultValue}.`
            : `Nothing set here yet — showing ${value}. Type to override.`
        }
      >
        {compareNote ? 'Compares — see override rows' : hintFor(source, defaultValue)}
      </p>
    </div>
  );
};
